import { routes } from '@/utils/routes';
import { Button, Flex, Link, Switch, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCookieConsentStore } from '@onbeam/utils';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import NextLink from 'next/link';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ConsentFormSchemaType } from '../CookieConsent/CookieConsent.types';
import {
  DEFAULT_FORM_VALUES,
  consentFormSchema,
  consentOptions,
  convertConsentToFormValues,
} from '../CookieConsent/CookieConsent.utils';

export const CookieConsentModal = () => {
  const { t } = useTranslation('common');
  const { updateConsent, consent } = useCookieConsentStore();

  const { register, handleSubmit } = useForm<ConsentFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(consentFormSchema),
    defaultValues: consent ? convertConsentToFormValues(consent) : DEFAULT_FORM_VALUES,
  });

  const onSubmit: SubmitHandler<ConsentFormSchemaType> = async ({ ad_consent, ...values }) => {
    updateConsent(
      {
        ad_personalization: ad_consent,
        ad_storage: ad_consent,
        ad_user_data: ad_consent,
        ...values,
      },
      window.location.hostname,
    );
  };

  return (
    <Flex as="form" direction="column" gap={6} onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap={3}>
        <Text>{t('cookie-modal.title')}</Text>
        <Text fontSize="sm" color="gray.6">
          <Trans
            i18nKey="common:cookie-modal.modal-description"
            components={{
              privacyLink: (
                <Link
                  as={NextLink}
                  target="_blank"
                  color="primary"
                  href={routes.article.privacyPolicy}
                />
              ),
            }}
          />
        </Text>
        {consentOptions.map(({ key, isDisabled, value }) => (
          <Flex direction="column" gap={1} key={key}>
            <Flex as="label" gap={2} justify="space-between" htmlFor={key}>
              <Text as="span" fontSize="md">
                {t(value.title)}
              </Text>
              <Switch id={key} {...register(key)} disabled={isDisabled} />
            </Flex>
            <Text as="span" fontSize="xs" color="gray.6">
              <Trans
                i18nKey={`common:${value.description}`}
                components={{
                  googleAdCenterLink: (
                    <Link
                      as={NextLink}
                      href="https://myadcenter.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                    />
                  ),
                }}
              />
            </Text>
          </Flex>
        ))}
      </Flex>
      <Button type="submit">{t('cookie-modal.save-label')}</Button>
    </Flex>
  );
};
